/* =============================================================
   Timantti — shared client JS
   - Reveal-on-scroll observer (auto-attaches to [data-tt-reveal])
   - <tt-countdown> custom element for launch FOMO
   - <tt-tabs> simple tablist for product story / FAQ blocks
   Vanilla only. No build step required.
   ============================================================= */

(function () {
  'use strict';

  // ---------- Reveal observer ----------
  // Adds 'is-visible' to .tt-reveal once it enters the viewport.
  // Uses a single shared observer to avoid creating one per element.
  const revealEls = document.querySelectorAll('.tt-reveal');
  if (revealEls.length && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target); // one-shot — don't keep observing
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    revealEls.forEach((el) => io.observe(el));
  } else {
    // Fallback for very old browsers — reveal everything immediately
    revealEls.forEach((el) => el.classList.add('is-visible'));
  }

  // ---------- Countdown custom element ----------
  // Usage: <tt-countdown datetime="2026-05-25T18:00:00+02:00"></tt-countdown>
  // Renders four cells (days / hours / minutes / seconds).
  // Stops at zero and dispatches a 'tt:countdown-complete' event so the
  // section can swap copy (e.g. "Live now") without a page reload.
  class TimanttiCountdown extends HTMLElement {
    connectedCallback() {
      const iso = this.getAttribute('datetime');
      if (!iso) return;
      this.target = new Date(iso).getTime();
      if (Number.isNaN(this.target)) return;

      this.labels = {
        d: this.getAttribute('label-days')    || 'Days',
        h: this.getAttribute('label-hours')   || 'Hours',
        m: this.getAttribute('label-minutes') || 'Minutes',
        s: this.getAttribute('label-seconds') || 'Seconds',
      };

      this.render();
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    render() {
      this.innerHTML = `
        <div class="tt-countdown__grid" role="timer" aria-live="polite">
          <div class="tt-countdown__cell"><span data-d>00</span><em>${this.labels.d}</em></div>
          <div class="tt-countdown__cell"><span data-h>00</span><em>${this.labels.h}</em></div>
          <div class="tt-countdown__cell"><span data-m>00</span><em>${this.labels.m}</em></div>
          <div class="tt-countdown__cell"><span data-s>00</span><em>${this.labels.s}</em></div>
        </div>`;
      this.$ = {
        d: this.querySelector('[data-d]'),
        h: this.querySelector('[data-h]'),
        m: this.querySelector('[data-m]'),
        s: this.querySelector('[data-s]'),
      };
    }

    tick() {
      const diff = this.target - Date.now();
      if (diff <= 0) {
        this.$.d.textContent = this.$.h.textContent =
          this.$.m.textContent = this.$.s.textContent = '00';
        clearInterval(this.timer);
        this.dispatchEvent(
          new CustomEvent('tt:countdown-complete', { bubbles: true })
        );
        this.classList.add('is-complete');
        return;
      }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      this.$.d.textContent = String(d).padStart(2, '0');
      this.$.h.textContent = String(h).padStart(2, '0');
      this.$.m.textContent = String(m).padStart(2, '0');
      this.$.s.textContent = String(s).padStart(2, '0');
    }
  }
  if (!customElements.get('tt-countdown')) {
    customElements.define('tt-countdown', TimanttiCountdown);
  }

  // ---------- Tabs custom element ----------
  // Usage:
  // <tt-tabs>
  //   <button role="tab" data-tt-tab="materials" aria-selected="true">Materials</button>
  //   <button role="tab" data-tt-tab="care">Care</button>
  //   <div data-tt-panel="materials">...</div>
  //   <div data-tt-panel="care" hidden>...</div>
  // </tt-tabs>
  class TimanttiTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = this.querySelectorAll('[data-tt-tab]');
      this.panels = this.querySelectorAll('[data-tt-panel]');
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab.dataset.ttTab));
      });
    }
    activate(name) {
      this.tabs.forEach((t) => {
        const on = t.dataset.ttTab === name;
        t.setAttribute('aria-selected', on);
        t.classList.toggle('is-active', on);
      });
      this.panels.forEach((p) => {
        p.hidden = p.dataset.ttPanel !== name;
      });
    }
  }
  if (!customElements.get('tt-tabs')) {
    customElements.define('tt-tabs', TimanttiTabs);
  }
})();
